import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './authStore';
import { login, logout, refreshAccessToken } from './auth';
import { queryClient } from './queryClient';
import styles from './AuthProvider.module.css';

/**
 * AuthContext — exposes the app-level auth state and actions.
 *
 * @typedef {{
 *   status: 'unknown' | 'anonymous' | 'authenticated',
 *   username: string | null,
 *   signIn: (credentials: { username: string, password: string }) => Promise<void>,
 *   signOut: () => Promise<void>,
 * }} AuthContextValue
 */

/** @type {React.Context<AuthContextValue | null>} */
const AuthContext = createContext(null);

/**
 * `useAuthContext` — consume auth context. Must be used inside `<AuthProvider>`.
 * @returns {AuthContextValue}
 */
export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext must be used inside <AuthProvider>');
  }
  return ctx;
}

/**
 * `AuthProvider` — app-level auth context provider.
 *
 * On mount, if the stored auth status is 'unknown' (refresh token persisted but no
 * access token in memory — the post-reload case), eagerly calls `refreshAccessToken()`
 * once to mint an access token. A ref guard prevents the double-invoke under React 18
 * StrictMode from firing a second refresh request.
 *
 * While status is 'unknown' (boot refresh in flight), renders a minimal restoring state
 * instead of children. Fresh visitors (status 'anonymous' at mount) never see it.
 *
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  const { status, username } = useAuth();

  // StrictMode guard: ensures refreshAccessToken is called AT MOST ONCE per real mount.
  const hasRefreshedRef = useRef(false);

  useEffect(() => {
    if (status !== 'unknown') return;
    if (hasRefreshedRef.current) return;
    hasRefreshedRef.current = true;

    refreshAccessToken().catch(() => {
      // Boot refresh failed (AuthError or transient). authStore already cleared session
      // (terminal path) → status becomes 'anonymous'. Swallow — the app shows sign-in.
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally no dep on `status`: we only want this to run once on mount.

  /** @type {AuthContextValue['signIn']} */
  const signIn = useCallback(async ({ username: u, password }) => {
    await login({ username: u, password });
    // authStore is updated by login → status flips reactively via useAuth()
  }, []);

  /** @type {AuthContextValue['signOut']} */
  const signOut = useCallback(async () => {
    try {
      await logout();
    } catch {
      // logout is best-effort — local session is cleared by logout() regardless
    }
    queryClient.clear();
  }, []);

  const booting = status === 'unknown';

  if (booting) {
    return (
      <div className={styles.restoring}>
        Restoring your session…
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ status, username, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
